/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires the optional prev/next buttons to scroll the track by roughly one page
 * and toggles their `disabled` state at the start/end of the scroll range. The track is a
 * native scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus
 * all work without JS — this component only enhances the arrow controls.
 *
 * Optional autoplay: add `autoplay="6000"` (milliseconds) to advance one item at a time,
 * looping back to the start at the end. Autoplay pauses while the pointer hovers the
 * carousel, while anything inside has focus, while the carousel is off-screen or the tab
 * is hidden, and for a while after any touch, wheel or arrow interaction. An optional
 * `<button data-carousel-toggle aria-pressed="false">` pauses/resumes it. It never runs
 * when the visitor prefers reduced motion.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel [autoplay="6000"]>
 *     [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
const INTERACTION_COOLDOWN = 8000;

class SCarousel extends HTMLElement {
  #timer = null;
  #hovered = false;
  #paused = false;
  #focused = false;
  #visible = false;
  #resumeAt = 0;
  #reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    if (!this.track) return;

    this.onPrev = () => {
      this.#noteInteraction();
      this.scrollByPage(-1);
    };
    this.onNext = () => {
      this.#noteInteraction();
      this.scrollByPage(1);
    };
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();
    this.#setupAutoplay();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.resizeObserver?.disconnect();
    this.autoplayAbort?.abort();
    this.intersectionObserver?.disconnect();
    this.#stop();
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = Math.abs(this.track.scrollLeft) <= 1;
    const atEnd = Math.abs(this.track.scrollLeft) >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }

  /** Interval in ms from the `autoplay` attribute, or 0 when autoplay is off. */
  get #interval() {
    if (!this.hasAttribute("autoplay")) return 0;
    const value = Number.parseInt(this.getAttribute("autoplay"), 10);
    return Number.isFinite(value) && value >= 2000 ? value : 6000;
  }

  #setupAutoplay() {
    if (!this.#interval) return;

    this.autoplayAbort = new AbortController();
    const opts = { signal: this.autoplayAbort.signal };

    this.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "mouse") this.#hovered = true;
    }, opts);
    this.addEventListener("pointerleave", (event) => {
      if (event.pointerType === "mouse") this.#hovered = false;
    }, opts);
    this.addEventListener("focusin", () => { this.#focused = true; }, opts);
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) this.#focused = false;
    }, opts);
    // Touch, pen, wheel and keyboard scrolling all count as "the visitor is using it".
    this.track.addEventListener("pointerdown", () => this.#noteInteraction(), { ...opts, passive: true });
    this.track.addEventListener("touchstart", () => this.#noteInteraction(), { ...opts, passive: true });
    this.track.addEventListener("wheel", () => this.#noteInteraction(), { ...opts, passive: true });
    this.track.addEventListener("keydown", () => this.#noteInteraction(), opts);
    this.#reducedMotion.addEventListener("change", () => this.#sync(), opts);

    // Optional visible pause/play control (WCAG 2.2.2). aria-pressed="true" means paused.
    this.toggleButton = this.querySelector("[data-carousel-toggle]");
    if (this.toggleButton) {
      this.toggleButton.hidden = this.#reducedMotion.matches;
      this.toggleButton.addEventListener("click", () => {
        this.#paused = !this.#paused;
        this.toggleButton.setAttribute("aria-pressed", String(this.#paused));
      }, opts);
    }
    document.addEventListener("visibilitychange", () => this.#sync(), opts);

    this.intersectionObserver = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#sync();
    }, { threshold: 0.35 });
    this.intersectionObserver.observe(this);
  }

  #noteInteraction() {
    this.#resumeAt = Date.now() + INTERACTION_COOLDOWN;
  }

  /** Run the timer only while the carousel is on screen, the tab is visible and motion is allowed. */
  #sync() {
    const shouldRun = this.#visible && !document.hidden && !this.#reducedMotion.matches;
    if (shouldRun) this.#start();
    else this.#stop();
  }

  #start() {
    if (this.#timer) return;
    this.#timer = window.setInterval(() => this.#tick(), this.#interval);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #tick() {
    if (this.#paused || this.#hovered || this.#focused || Date.now() < this.#resumeAt) return;

    const track = this.track;
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 1) return;

    const position = Math.abs(track.scrollLeft);
    if (position >= maxScroll - 1) {
      track.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    // Advance by exactly one item (its width plus the track's gap) so snap lands cleanly.
    const first = track.firstElementChild;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    const direction = getComputedStyle(track).direction === "rtl" ? -1 : 1;
    track.scrollBy({ left: step * direction, behavior: "smooth" });
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
